import {getIn, useFormikContext} from 'formik';
import {NySelectInput, NyTextInput} from 'nyse-web-tools-common/lib/react';
import type {NyFieldInput} from 'nyse-web-tools-common/lib/react/types';
import React, {useMemo} from 'react';

import useClearingNumberList from '../../hooks/useClearingNumberList';
import {combineOptionsWithDbValue} from './selectUtils';

type ClearingNumberByMpidSelectProps = {
    crd: number;
    mpidFieldName: string;
    isMulti?: boolean;
} & Partial<NyFieldInput>;

/**
 * ClearingNumberByMpidSelect - A select component that filters clearing numbers
 * based on the selected MPID in the form.
 *
 * This component watches the MPID field value and dynamically updates the available
 * clearing numbers to only show those associated with the selected MPID from GBO reference data.
 */
export const ClearingNumberByMpidSelect = React.memo<ClearingNumberByMpidSelectProps>(
    (props) => {
        const formik = useFormikContext();

        // Get the current MPID value from the form (using getIn for nested field paths)
        const selectedMpid = useMemo(
            () => getIn(formik.values, props.mpidFieldName),
            [formik.values, props.mpidFieldName]
        );

        // Get the current clearing number value from the form for initialOption
        const currentClearingNumber = useMemo(
            () => getIn(formik.values, props.name),
            [formik.values, props.name]
        );

        // Fetch ALL clearing numbers for the selected MPID from GBO reference data
        const clearingNumberList = useClearingNumberList(props.crd, selectedMpid);

        // Get all clearing numbers from GBO reference data (not just the default one)
        const clearingNumbers = useMemo(() => {
            if (!selectedMpid) {
                return [];
            }

            // Get clearing numbers from GBO reference data
            const gboData = clearingNumberList?.data as any;

            // Filter to only include active clearing numbers
            // If status field exists, only include 'active' entries
            // If status field is missing (default case), include the entry
            const clearingNumbersFromGBO = (gboData?.clearingDetails ?? [])
                .filter((detail) => {
                    // If no status field exists, assume active (backward compatibility)
                    if (!detail.status) {
                        return true;
                    }
                    // If status exists, only include active entries
                    return detail.status.toLowerCase() === 'active';
                })
                .map(({clearingNumber}) => ({
                    label: clearingNumber.toString(),
                    value: clearingNumber
                }));

            return clearingNumbersFromGBO;
        }, [clearingNumberList?.data, selectedMpid]);

        // Combine clearing numbers with the current value to ensure it's always available in options
        const initialOption = useMemo(
            () => ({clearingNumber: currentClearingNumber ?? ''}),
            [currentClearingNumber]
        );

        const options = useMemo(
            () => {
                // If fetching and no data yet, show loading placeholder
                const isFetching = clearingNumberList?.isFetching || clearingNumberList?.isLoading;
                if (isFetching && clearingNumbers.length === 0) {
                    return [
                        {
                            label: 'Loading...',
                            value: '',
                            isDisabled: true
                        }
                    ];
                }

                const baseOptions = [
                    ...(props.isMulti || props.required
                        ? []
                        : [
                            {
                                label: '-',
                                value: ''
                            }
                        ]),
                    ...clearingNumbers
                ];

                // Combine with current value to ensure it's in the dropdown
                return combineOptionsWithDbValue(initialOption, baseOptions, 'clearingNumber');
            },
            [clearingNumbers, props.isMulti, props.required, initialOption, clearingNumberList?.isLoading, clearingNumberList?.isFetching]
        );

        // If no MPID is selected, show a text input with a message
        if (!selectedMpid) {
            return (
                <NyTextInput
                    {...props}
                    name={props.name}
                    disabled={true}
                    placeholder='Select MPID first'
                />
            );
        }

        return (
            <NySelectInput
                disabled={props.disabled}
                initialValue={props.initialValue}
                isLoading={clearingNumberList?.isFetching || clearingNumberList?.isLoading}
                isMulti={props.isMulti}
                name={props.name}
                onChange={props.onChange}
                options={options}
                required={props.required}
            />
        );
    }
);
